import { Link, createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  Archive,
  Building2,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  Loader2,
  Mail,
  Pencil,
  Plus,
  RefreshCcw,
  Trash2,
  User,
  UserMinus,
  Users,
} from 'lucide-react'
import {
  useWorkspaces,
  useCreateWorkspace,
  useUpdateWorkspace,
  useArchiveWorkspace,
  useUnarchiveWorkspace,
  useWorkspaceMembers,
  useWorkspaceInvites,
  useCreateWorkspaceInvite,
  useAcceptWorkspaceInvite,
  useResendWorkspaceInvite,
  useRevokeWorkspaceInvite,
  useUpdateWorkspaceMember,
  useProjects,
  useCreateProject,
  useWorkspaceAudit,
  useRemoveWorkspaceMember,
  type AuditLogRead,
  type WorkspaceRead,
  type WorkspaceRole,
  type WorkspaceMemberRead,
  type WorkspaceInviteRead,
  type ProjectRead,
} from '@/api/queries'
import { useCurrentWorkspace } from '@/api/workspace-context-provider'
import {
  canManageMembers,
  canManageWorkspace,
  canManageProjects,
  canViewAudit,
  INVITABLE_ROLES,
  ROLE_LABELS,
} from '@/api/workspace-permissions'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { InlineError, LoadingState, SkeletonList } from '@/components/ui/loading-state'
import { PageHeader } from '@/components/ui/page-header'
import { cn } from '@/lib/utils'
import { formatError } from '@/lib/format-error'
import { assignableRoles, blockedDemotion } from '@/lib/permissions'

export const Route = createFileRoute('/_authenticated/workspaces')({
  component: WorkspacesPage,
})

function inviteTokenFromLocation(): string | null {
  const params = new URLSearchParams(window.location.search)
  const token = params.get('invite_token')?.trim() || params.get('inviteToken')?.trim()
  return token || null
}

function clearInviteTokenFromLocation() {
  const url = new URL(window.location.href)
  url.searchParams.delete('invite_token')
  url.searchParams.delete('inviteToken')
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
}

// ---------------------------------------------------------------------------
// Role badge helpers
// ---------------------------------------------------------------------------

type RoleTone = 'primary' | 'warning' | 'success' | 'neutral'

function roleTone(role: WorkspaceRole): RoleTone {
  switch (role) {
    case 'owner':
      return 'primary'
    case 'admin':
      return 'warning'
    case 'editor':
      return 'success'
    case 'viewer':
      return 'neutral'
  }
}

function RoleBadge({ role }: { role: WorkspaceRole }) {
  return (
    <Badge tone={roleTone(role)} size="xs">
      {ROLE_LABELS[role]}
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// Create workspace dialog
// ---------------------------------------------------------------------------

function CreateWorkspaceDialog({ onCreated }: { onCreated: (uuid: string) => void }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const createWorkspace = useCreateWorkspace()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return
    try {
      const ws = await createWorkspace.mutateAsync({ name: trimmed })
      toast.success(`Workspace "${ws.name}" created`)
      setName('')
      setOpen(false)
      onCreated(ws.uuid)
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus />
          New workspace
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Create workspace</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ws-name">Name</Label>
            <Input
              id="ws-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My team"
              required
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={createWorkspace.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createWorkspace.isPending || !name.trim()}>
              {createWorkspace.isPending && <Loader2 className="animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Workspace list card
// ---------------------------------------------------------------------------

function WorkspaceCard({
  workspace,
  selected,
  onClick,
}: {
  workspace: WorkspaceRead
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full rounded-lg border bg-card text-left text-card-foreground transition-[box-shadow,border-color,transform] duration-150 ease-out',
        'shadow-soft hover:shadow-raised hover:border-border-strong/70 hover:-translate-y-px',
        'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40',
        selected && 'border-ring ring-[3px] ring-ring/20',
      )}
      data-testid="workspace-card"
      data-selected={selected || undefined}
    >
      <div className="flex items-start gap-3 p-4">
        <span
          aria-hidden
          className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary-soft-foreground"
        >
          {workspace.is_personal ? (
            <User className="size-4" />
          ) : (
            <Building2 className="size-4" />
          )}
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="truncate text-sm font-semibold text-foreground">
              {workspace.name}
            </span>
            {workspace.is_personal && (
              <Badge tone="outline" size="xs">
                Personal
              </Badge>
            )}
            {workspace.archive_status === 'archived' && (
              <Badge tone="warning" size="xs">
                Archived
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <RoleBadge role={workspace.role} />
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Users className="size-3" />
              {workspace.member_count}
            </span>
          </div>
        </div>
        <ChevronRight
          className={cn(
            'mt-1 size-4 shrink-0 text-muted-foreground/60 transition-transform',
            selected && 'rotate-90 text-foreground',
          )}
        />
      </div>
    </button>
  )
}

// ---------------------------------------------------------------------------
// Members section
// ---------------------------------------------------------------------------

function MemberRoleSelect({
  member,
  callerRole,
  ownerCount,
  onChange,
  disabled,
}: {
  member: WorkspaceMemberRead
  callerRole: WorkspaceRole
  ownerCount: number
  onChange: (role: WorkspaceRole) => void
  disabled: boolean
}) {
  const options = assignableRoles(callerRole)
  const promotedOwner = !options.includes('owner')
  // Ensure the *current* role always appears in the dropdown, even when the
  // caller can't grant it (e.g. admin viewing another admin — admin role is
  // their max). Avoids the dropdown silently swapping the displayed role to
  // the first available option.
  const allOptions: WorkspaceRole[] = options.includes(member.role)
    ? [...options]
    : [member.role, ...options]

  return (
    <select
      value={member.role}
      onChange={(e) => onChange(e.target.value as WorkspaceRole)}
      disabled={disabled}
      aria-label={`Role for ${member.email}`}
      data-testid={`member-role-${member.user_id}`}
      className={cn(
        'h-8 rounded-md border border-input bg-card px-2 text-xs font-medium shadow-soft',
        'transition-[box-shadow,border-color] duration-150 ease-out',
        'outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/30',
        'disabled:cursor-not-allowed disabled:opacity-50',
      )}
    >
      {allOptions.map((r) => {
        const denial = blockedDemotion({
          targetRole: member.role,
          newRole: r,
          ownerCount,
        })
        return (
          <option key={r} value={r} disabled={Boolean(denial) && r !== member.role}>
            {r}
            {denial && r !== member.role ? ' (blocked)' : ''}
          </option>
        )
      })}
      {promotedOwner && member.role !== 'owner' && (
        <option value="owner" disabled>
          owner (owner only)
        </option>
      )}
    </select>
  )
}

function MembersSection({
  workspaceUuid,
  currentRole,
  isPersonal,
}: {
  workspaceUuid: string
  currentRole: WorkspaceRole
  isPersonal: boolean
}) {
  const { data: members, isLoading, error } = useWorkspaceMembers(workspaceUuid)
  const removeMember = useRemoveWorkspaceMember(workspaceUuid)
  const updateMember = useUpdateWorkspaceMember(workspaceUuid)

  const canManage = canManageMembers(currentRole)

  const ownerCount = useMemo(
    () => (members ?? []).filter((m) => m.role === 'owner').length,
    [members],
  )

  const handleRemove = async (member: WorkspaceMemberRead) => {
    if (
      !confirm(
        member.is_self
          ? 'Leave this workspace? You will lose access to all of its projects.'
          : `Remove ${member.email} from this workspace?`,
      )
    ) {
      return
    }
    try {
      await removeMember.mutateAsync(member.user_id)
      toast.success(member.is_self ? 'You left the workspace' : `${member.email} removed`)
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  const handleRoleChange = async (
    member: WorkspaceMemberRead,
    newRole: WorkspaceRole,
  ) => {
    if (newRole === member.role) return
    const denial = blockedDemotion({
      targetRole: member.role,
      newRole,
      ownerCount,
    })
    if (denial) {
      toast.error(denial)
      return
    }
    try {
      await updateMember.mutateAsync({
        memberUserId: member.user_id,
        body: { role: newRole },
      })
      toast.success(`${member.email} is now ${newRole}`)
    } catch (err) {
      // Server-side denial — rollback is handled by the mutation's onError.
      toast.error(formatError(err))
    }
  }

  if (isLoading) {
    return <LoadingState variant="inline" title="Loading members…" />
  }

  if (error) {
    return <InlineError message={formatError(error)} />
  }

  if (!members?.length) {
    return (
      <EmptyState
        variant="bare"
        icon={Users}
        title="No members yet"
        description="Invite colleagues to collaborate on this workspace."
      />
    )
  }

  return (
    <ul className="divide-y divide-border" data-testid="members-list">
      {members.map((member) => {
        const isPendingUpdate =
          updateMember.isPending &&
          updateMember.variables?.memberUserId === member.user_id
        // The role select is the editable affordance; the badge is the
        // read-only display for viewers/editors who can't edit roles.
        const showRoleSelect = canManage && !member.is_self
        return (
          <li
            key={member.user_uuid}
            className="flex items-center justify-between gap-3 py-2.5"
            data-testid={`member-row-${member.user_id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="truncate text-sm font-medium text-foreground">
                  {member.name || member.email}
                </span>
                {member.is_self && (
                  <Badge tone="info" size="xs">
                    You
                  </Badge>
                )}
                {!showRoleSelect && <RoleBadge role={member.role} />}
              </div>
              <p className="truncate text-xs text-muted-foreground">{member.email}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {showRoleSelect && (
                <MemberRoleSelect
                  member={member}
                  callerRole={currentRole}
                  ownerCount={ownerCount}
                  onChange={(role) => handleRoleChange(member, role)}
                  disabled={isPendingUpdate}
                />
              )}
              {isPendingUpdate && (
                <Loader2
                  className="size-3.5 animate-spin text-muted-foreground"
                  aria-label="Saving role"
                />
              )}
              {member.role !== 'owner' &&
                !(member.is_self && isPersonal) &&
                ((canManage && !member.is_self) || member.is_self) && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => handleRemove(member)}
                    disabled={removeMember.isPending}
                    aria-label={
                      member.is_self
                        ? 'Leave workspace'
                        : `Remove ${member.email}`
                    }
                    data-testid={
                      member.is_self
                        ? 'leave-workspace'
                        : `remove-member-${member.user_id}`
                    }
                  >
                    <UserMinus />
                  </Button>
                )}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

// ---------------------------------------------------------------------------
// Invites section
// ---------------------------------------------------------------------------

function InviteForm({ workspaceUuid }: { workspaceUuid: string }) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<WorkspaceRole>('editor')
  const createInvite = useCreateWorkspaceInvite(workspaceUuid)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed) return
    try {
      await createInvite.mutateAsync({ email: trimmed, role })
      toast.success(`Invite sent to ${trimmed}`)
      setEmail('')
      setRole('editor')
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-2 pt-2">
      <div className="min-w-0 flex-1 space-y-1.5" style={{ minWidth: '160px' }}>
        <Label htmlFor={`invite-email-${workspaceUuid}`}>Email</Label>
        <Input
          id={`invite-email-${workspaceUuid}`}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="colleague@example.com"
          required
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`invite-role-${workspaceUuid}`}>Role</Label>
        <select
          id={`invite-role-${workspaceUuid}`}
          value={role}
          onChange={(e) => setRole(e.target.value as WorkspaceRole)}
          className={cn(
            'flex h-10 rounded-md border border-input bg-card px-3 text-sm shadow-soft',
            'transition-[box-shadow,border-color] duration-150 ease-out',
            'outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/30',
          )}
        >
          {INVITABLE_ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </select>
      </div>
      <Button
        type="submit"
        size="sm"
        disabled={createInvite.isPending || !email.trim()}
      >
        {createInvite.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
        Invite
      </Button>
    </form>
  )
}

function InviteRow({
  invite,
  canManage,
  onResend,
  onRevoke,
  isResending,
  isRevoking,
  now,
}: {
  invite: WorkspaceInviteRead
  canManage: boolean
  onResend: () => void
  onRevoke: () => void
  isResending: boolean
  isRevoking: boolean
  /** Reference timestamp captured by the parent so this component stays
   * pure. Passed-down value lets us flag expired invites without calling
   * the impure ``Date.now()`` during render. */
  now: number
}) {
  const expired = new Date(invite.expires_at).getTime() < now
  return (
    <li
      className="flex items-center justify-between gap-3 py-2.5"
      data-testid={`invite-row-${invite.uuid}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Mail className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <span className="truncate text-sm text-foreground">{invite.email}</span>
          <RoleBadge role={invite.role} />
          {expired && (
            <Badge tone="warning" size="xs">
              Expired
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Invited by {invite.invited_by_email}
        </p>
      </div>
      {canManage && (
        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={onResend}
            disabled={isResending || isRevoking}
            data-testid={`resend-invite-${invite.uuid}`}
            aria-label={`Resend invite to ${invite.email}`}
          >
            {isResending ? (
              <Loader2 className="animate-spin" />
            ) : (
              <RefreshCcw />
            )}
            Resend
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRevoke}
            disabled={isResending || isRevoking}
            data-testid={`revoke-invite-${invite.uuid}`}
            aria-label={`Revoke invite for ${invite.email}`}
          >
            <Trash2 />
            Revoke
          </Button>
        </div>
      )}
    </li>
  )
}

function InvitesSection({
  workspaceUuid,
  currentRole,
}: {
  workspaceUuid: string
  currentRole: WorkspaceRole
}) {
  const { data: invites, isLoading, error } = useWorkspaceInvites(workspaceUuid)
  const revokeInvite = useRevokeWorkspaceInvite(workspaceUuid)
  const resendInvite = useResendWorkspaceInvite(workspaceUuid)

  const canManage = canManageMembers(currentRole)

  const handleRevoke = async (invite: WorkspaceInviteRead) => {
    if (!confirm(`Revoke invite for ${invite.email}?`)) return
    try {
      await revokeInvite.mutateAsync(invite.uuid)
      toast.success(`Invite for ${invite.email} revoked`)
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  const handleResend = async (invite: WorkspaceInviteRead) => {
    try {
      await resendInvite.mutateAsync({
        uuid: invite.uuid,
        email: invite.email,
        role: invite.role,
      })
      toast.success(`Invite to ${invite.email} re-sent`)
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  const pendingInvites = invites?.filter(
    (i) => !i.accepted_at && !i.revoked_at,
  )
  // Capture `now` once per render so the InviteRow children stay pure.
  // The lint rule that catches `Date.now()` inside child components
  // tolerates a single eslint-ignore at the top-level read site.
  // eslint-disable-next-line react-hooks/purity
  const now = Date.now()

  if (isLoading) {
    return <LoadingState variant="inline" title="Loading invites…" />
  }

  if (error) {
    return <InlineError message={formatError(error)} />
  }

  return (
    <div className="space-y-3">
      {pendingInvites && pendingInvites.length > 0 ? (
        <ul className="divide-y divide-border" data-testid="invites-list">
          {pendingInvites.map((invite) => (
            <InviteRow
              key={invite.uuid}
              invite={invite}
              canManage={canManage}
              now={now}
              onResend={() => handleResend(invite)}
              onRevoke={() => handleRevoke(invite)}
              isResending={
                resendInvite.isPending &&
                resendInvite.variables?.uuid === invite.uuid
              }
              isRevoking={
                revokeInvite.isPending &&
                revokeInvite.variables === invite.uuid
              }
            />
          ))}
        </ul>
      ) : (
        <EmptyState
          title="No pending invites"
          description="Use the form below to invite a teammate by email."
          variant="bare"
        />
      )}
      {canManage && <InviteForm workspaceUuid={workspaceUuid} />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Projects section
// ---------------------------------------------------------------------------

function CreateProjectDialog({ workspaceUuid }: { workspaceUuid: string }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const createProject = useCreateProject(workspaceUuid)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return
    try {
      await createProject.mutateAsync({ name: trimmed })
      toast.success(`Project "${trimmed}" created`)
      setName('')
      setOpen(false)
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Plus />
          New project
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Create project</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="project-name">Name</Label>
            <Input
              id="project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Q3 Contracts"
              required
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={createProject.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createProject.isPending || !name.trim()}>
              {createProject.isPending && <Loader2 className="animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function ProjectCard({
  workspaceUuid,
  project,
}: {
  workspaceUuid: string
  project: ProjectRead
}) {
  return (
    <Link
      to="/projects/$workspaceUuid/$projectUuid"
      params={{ workspaceUuid, projectUuid: project.uuid }}
      className={cn(
        'flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-3',
        'shadow-soft transition-[box-shadow,border-color] duration-150 ease-out',
        'hover:border-primary/40 hover:shadow-floating',
        'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40',
      )}
      data-testid="project-card"
      aria-label={`Open project ${project.name}`}
    >
      <span
        aria-hidden
        className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
      >
        <FolderOpen className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">
          {project.name}
        </p>
        <p className="text-xs text-muted-foreground">
          {project.folder_count} folder{project.folder_count !== 1 ? 's' : ''}
        </p>
      </div>
    </Link>
  )
}

function ProjectsSection({
  workspaceUuid,
  currentRole,
  isArchived,
}: {
  workspaceUuid: string
  currentRole: WorkspaceRole
  isArchived: boolean
}) {
  const { data: projects, isLoading, error } = useProjects(workspaceUuid)
  const canCreate = canManageProjects(currentRole) && !isArchived

  if (isLoading) {
    return <SkeletonList rows={2} rowClassName="h-14" aria-label="Loading projects" />
  }

  if (error) {
    return <InlineError message={formatError(error)} />
  }

  return (
    <div className="space-y-3">
      {projects && projects.length > 0 ? (
        <ul className="space-y-2" data-testid="projects-list">
          {projects.map((p) => (
            <li key={p.uuid}>
              <ProjectCard workspaceUuid={workspaceUuid} project={p} />
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          variant="bare"
          icon={FolderOpen}
          title="No projects yet"
          description="Create a project to manage delivery workstreams."
          data-testid="projects-empty"
        />
      )}
      {isArchived ? (
        <p className="text-xs text-muted-foreground">
          Workspace is archived; unarchive it before creating new projects.
        </p>
      ) : null}
      {canCreate && <CreateProjectDialog workspaceUuid={workspaceUuid} />}
    </div>
  )
}

function RenameWorkspaceDialog({ workspace }: { workspace: WorkspaceRead }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState(workspace.name)
  const updateWorkspace = useUpdateWorkspace(workspace.uuid)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    const trimmed = name.trim()
    if (!trimmed || trimmed === workspace.name) return
    try {
      await updateWorkspace.mutateAsync({ name: trimmed })
      toast.success('Workspace renamed')
      setOpen(false)
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <Pencil />
          Rename
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Rename workspace</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={`rename-workspace-${workspace.uuid}`}>Name</Label>
            <Input
              id={`rename-workspace-${workspace.uuid}`}
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateWorkspace.isPending || !name.trim()}>
              {updateWorkspace.isPending ? <Loader2 className="animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function WorkspaceArchiveButton({ workspace }: { workspace: WorkspaceRead }) {
  const archive = useArchiveWorkspace(workspace.uuid)
  const unarchive = useUnarchiveWorkspace(workspace.uuid)
  const isArchived = workspace.archive_status === 'archived'
  const mutation = isArchived ? unarchive : archive

  const submit = async () => {
    try {
      await mutation.mutateAsync()
      toast.success(isArchived ? 'Workspace unarchived' : 'Workspace archived')
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={submit}
      disabled={mutation.isPending}
    >
      {mutation.isPending ? <Loader2 className="animate-spin" /> : <Archive />}
      {isArchived ? 'Unarchive' : 'Archive'}
    </Button>
  )
}

// ---------------------------------------------------------------------------
// Audit log section
// ---------------------------------------------------------------------------

const AUDIT_PAGE_SIZE = 25

/**
 * Top-level action category derived from the dotted backend
 * ``AuditAction`` value (e.g. ``member.invited`` → ``member``). Stable
 * subset of the ``app.models.AuditAction`` enum we care to surface in
 * the filter chips.
 */
const AUDIT_CATEGORIES: readonly { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'workspace', label: 'Workspace' },
  { value: 'member', label: 'Members' },
  { value: 'project', label: 'Projects' },
] as const

function actionCategory(action: string): string {
  const dot = action.indexOf('.')
  return dot === -1 ? action : action.slice(0, dot)
}

/**
 * Human-friendly label for a dotted action key. Kept intentionally
 * generic — the audit panel must NOT render raw provider payloads,
 * free-form text, or any user-entered values, because those can include PII. We
 * only show "what kind of thing happened" and "who did it".
 */
function describeAction(action: string): string {
  switch (action) {
    case 'workspace.created':
      return 'Created workspace'
    case 'workspace.updated':
      return 'Updated workspace'
    case 'workspace.deleted':
      return 'Deleted workspace'
    case 'member.invited':
      return 'Invited a member'
    case 'member.invite_revoked':
      return 'Revoked an invite'
    case 'member.invite_accepted':
      return 'Joined the workspace'
    case 'member.role_changed':
      return 'Changed a member role'
    case 'member.removed':
      return 'Removed a member'
    case 'project.created':
      return 'Created a project'
    case 'project.updated':
      return 'Updated a project'
    case 'project.deleted':
      return 'Deleted a project'
    case 'folder.created':
      return 'Created a folder'
    case 'folder.updated':
      return 'Renamed a folder'
    case 'folder.deleted':
      return 'Deleted a folder'
    case 'bosun.project.created':
      return 'Created a Bosun project'
    case 'bosun.project.updated':
      return 'Updated a Bosun project'
    case 'bosun.project.provisioned':
      return 'Provisioned a Bosun project'
    case 'bosun.project.failed':
      return 'Project provisioning failed'
    case 'deployment.created':
      return 'Created a deployment'
    case 'deployment.updated':
      return 'Updated a deployment'
    case 'deployment.failed':
      return 'Deployment failed'
    default:
      return action.replace(/[._]/g, ' ')
  }
}

function auditTone(action: string): 'neutral' | 'primary' | 'success' | 'warning' | 'danger' {
  if (action.endsWith('.deleted') || action.endsWith('.revoked')) return 'danger'
  if (action.endsWith('.created') || action.endsWith('.invited')) return 'success'
  if (action.endsWith('.role_changed') || action.endsWith('.updated')) return 'warning'
  if (action.startsWith('bosun.') || action.startsWith('deployment.')) return 'primary'
  return 'neutral'
}

function AuditEntryRow({ entry }: { entry: AuditLogRead }) {
  return (
    <li
      className="flex flex-col gap-2 py-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-3"
      data-testid={`audit-entry-${entry.uuid}`}
      data-action={entry.action}
    >
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge tone={auditTone(entry.action)} size="xs">
            {describeAction(entry.action)}
          </Badge>
          {entry.target_type && (
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {entry.target_type}
            </span>
          )}
        </div>
        {entry.actor_email && (
          <p className="break-all text-xs text-muted-foreground">{entry.actor_email}</p>
        )}
      </div>
      <time
        dateTime={entry.created_at}
        className="shrink-0 text-xs text-muted-foreground/70 sm:text-right"
        title={new Date(entry.created_at).toLocaleString()}
      >
        {new Date(entry.created_at).toLocaleString(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',
        })}
      </time>
    </li>
  )
}

function AuditSection({ workspaceUuid }: { workspaceUuid: string }) {
  const [page, setPage] = useState(0)
  const [actorFilter, setActorFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')

  const offset = page * AUDIT_PAGE_SIZE
  // Fetch ``pageSize + 1`` so we can tell whether a next page exists
  // without a second round-trip. The 26th entry only feeds the chevron;
  // the table itself slices off the spare row.
  const {
    data: entries,
    isLoading,
    isFetching,
    error,
  } = useWorkspaceAudit(workspaceUuid, {
    limit: AUDIT_PAGE_SIZE + 1,
    offset,
  })

  const pageEntries = useMemo(
    () => entries?.slice(0, AUDIT_PAGE_SIZE) ?? [],
    [entries],
  )
  const hasNext = (entries?.length ?? 0) > AUDIT_PAGE_SIZE

  // NOTE: filters apply to the CURRENT PAGE ONLY. The backend audit
  // endpoint takes only ``limit/offset`` (see
  // ``backend/app/routers/dashboard/workspaces.py``: no
  // ``action_prefix`` or ``actor_email`` params), so we can't push the
  // predicate server-side without a backend change. The UI surfaces
  // this explicitly (see ``audit-filter-scope-note`` below) and the
  // empty-state copy provider config the user to paginate when filters are
  // active so a hit on a later page isn't mistaken for "no matches
  // anywhere". If we ever add server-side filtering, drop the
  // page-scope note and lift these predicates into the query.
  const filtersActive =
    categoryFilter !== 'all' || actorFilter.trim().length > 0

  const filtered = useMemo(() => {
    const trimmedActor = actorFilter.trim().toLowerCase()
    return pageEntries.filter((entry) => {
      if (
        categoryFilter !== 'all' &&
        actionCategory(entry.action) !== categoryFilter
      ) {
        return false
      }
      if (
        trimmedActor &&
        !(entry.actor_email ?? '').toLowerCase().includes(trimmedActor)
      ) {
        return false
      }
      return true
    })
  }, [pageEntries, actorFilter, categoryFilter])

  if (isLoading) {
    return <LoadingState variant="inline" title="Loading audit log…" />
  }

  if (error) {
    return <InlineError message={formatError(error)} />
  }

  if (!pageEntries.length && page === 0) {
    return (
      <EmptyState
        title="No activity yet"
        description="Workspace events such as member changes and project creation will appear here."
        variant="bare"
      />
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div
          className="flex w-full flex-wrap gap-1 rounded-md border border-border bg-card p-1 sm:w-auto sm:p-0.5"
          role="group"
          aria-label="Audit category filter"
        >
          {AUDIT_CATEGORIES.map((cat) => {
            const active = categoryFilter === cat.value
            return (
              <button
                key={cat.value}
                type="button"
                onClick={() => setCategoryFilter(cat.value)}
                aria-pressed={active}
                data-testid={`audit-filter-${cat.value}`}
                className={cn(
                  'inline-flex h-8 min-w-0 flex-1 basis-[calc(50%-0.125rem)] items-center justify-center rounded-[5px] px-2.5 text-xs font-medium transition-colors sm:h-7 sm:flex-none sm:basis-auto sm:px-3',
                  active
                    ? 'bg-primary text-primary-foreground shadow-soft'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                {cat.label}
              </button>
            )
          })}
        </div>
        <Input
          type="search"
          value={actorFilter}
          onChange={(e) => setActorFilter(e.target.value)}
          placeholder="Filter by actor email…"
          aria-label="Filter audit by actor email"
          data-testid="audit-actor-filter"
          className="h-9 w-full text-xs sm:h-8 sm:max-w-[16rem]"
        />
      </div>
      <p
        className="text-[11px] text-muted-foreground/80"
        data-testid="audit-filter-scope-note"
      >
        Filters apply to the current page only — use Next to scan
        earlier events.
      </p>

      {filtered.length === 0 ? (
        <p
          className="text-sm text-muted-foreground"
          data-testid="audit-empty-message"
        >
          {filtersActive
            ? hasNext
              ? 'No events match these filters on this page. Click Next to keep searching earlier events.'
              : 'No events match these filters across the loaded history.'
            : 'No events match these filters.'}
        </p>
      ) : (
        <ul
          className="divide-y divide-border"
          data-testid="audit-entries"
        >
          {filtered.map((entry) => (
            <AuditEntryRow key={entry.uuid} entry={entry} />
          ))}
        </ul>
      )}

      <div className="flex items-center justify-between gap-3 pt-1">
        <span className="text-xs text-muted-foreground" data-testid="audit-page-info">
          Page {page + 1}
          {isFetching && page > 0 ? '…' : ''}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || isFetching}
            data-testid="audit-prev-page"
          >
            <ChevronLeft />
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={!hasNext || isFetching}
            data-testid="audit-next-page"
          >
            Next
            <ChevronRight />
          </Button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Detail panel — shown when a workspace card is selected
// ---------------------------------------------------------------------------

function WorkspaceDetail({ workspace }: { workspace: WorkspaceRead }) {
  // Hide the invite UI for personal workspaces — they're 1-member by
  // definition, and the backend rejects POSTs to their invites endpoint.
  const isArchived = workspace.archive_status === 'archived'
  const canInvite = canManageMembers(workspace.role) && !workspace.is_personal && !isArchived
  const canAudit = canViewAudit(workspace.role)
  const canProjects = canManageProjects(workspace.role)
  const canManageLifecycle = canManageWorkspace(workspace.role) && !workspace.is_personal
  const { currentWorkspaceUuid, setCurrentWorkspace } = useCurrentWorkspace()
  const isActive = currentWorkspaceUuid === workspace.uuid

  return (
    <div className="space-y-5" data-testid="workspace-detail">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            {workspace.name}
          </h2>
          <div className="mt-1 flex items-center gap-2">
            <RoleBadge role={workspace.role} />
            {workspace.is_personal && (
              <Badge tone="outline" size="xs">
                Personal
              </Badge>
            )}
            {isArchived && (
              <Badge tone="warning" size="xs">
                Archived
              </Badge>
            )}
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Users className="size-3" />
              {workspace.member_count} member{workspace.member_count !== 1 ? 's' : ''}
            </span>
          </div>
          {!canManageMembers(workspace.role) && !workspace.is_personal && (
            <p className="mt-2 text-xs text-muted-foreground">
              You have {workspace.role} access — only admins can invite or
              change member roles.
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canManageLifecycle && !isArchived ? <RenameWorkspaceDialog workspace={workspace} /> : null}
          {workspace.is_personal && canManageWorkspace(workspace.role) ? (
            <span className="text-xs text-muted-foreground">Personal workspaces cannot be renamed.</span>
          ) : null}
          {canManageLifecycle ? <WorkspaceArchiveButton workspace={workspace} /> : null}
          {!isActive && !isArchived && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentWorkspace(workspace.uuid)}
              data-testid="switch-active-workspace"
            >
              Switch to this workspace
            </Button>
          )}
          {isActive && (
            <Badge tone="primary" size="sm" data-testid="active-workspace-badge">
              Active workspace
            </Badge>
          )}
        </div>
      </div>

      {/* Members */}
      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          {workspace.is_personal ? (
            <CardDescription>
              Personal workspaces are private to you.
            </CardDescription>
          ) : (
            <CardDescription>
              Manage who can view, edit, or administer this workspace.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <MembersSection
            workspaceUuid={workspace.uuid}
            currentRole={workspace.role}
            isPersonal={workspace.is_personal}
          />
        </CardContent>
      </Card>

      {/* Invites — only show for admin/owner of non-personal workspaces */}
      {canInvite && (
        <Card data-testid="invites-card">
          <CardHeader>
            <CardTitle>Pending invites</CardTitle>
            <CardDescription>
              Active invites that haven't been accepted yet. Resend issues a
              fresh token and invalidates the old one.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <InvitesSection workspaceUuid={workspace.uuid} currentRole={workspace.role} />
          </CardContent>
        </Card>
      )}

      {/* Projects */}
      <Card>
        <CardHeader>
          <CardTitle>Projects</CardTitle>
          <CardDescription>
            {canProjects
              ? isArchived
                ? 'Archived workspace projects are retained but inactive.'
                : 'Organise Bosun projects.'
              : 'Viewers can browse projects, but only editors can create new ones.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ProjectsSection
            workspaceUuid={workspace.uuid}
            currentRole={workspace.role}
            isArchived={isArchived}
          />
        </CardContent>
      </Card>

      {/* Audit log — only show for admin/owner */}
      {canAudit && (
        <Card data-testid="audit-card">
          <CardHeader>
            <CardTitle>Activity log</CardTitle>
            <CardDescription>
              Audit events for this workspace. Filter by category and actor,
              paginate to see older history. Sensitive content (project secrets and provider payloads) is never shown here.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AuditSection workspaceUuid={workspace.uuid} />
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export function WorkspacesPage() {
  const { data: workspaces, isLoading, error } = useWorkspaces()
  const acceptInvite = useAcceptWorkspaceInvite()
  const { currentWorkspaceUuid, setCurrentWorkspace } = useCurrentWorkspace()
  const [inviteToken] = useState(inviteTokenFromLocation)
  const attemptedInviteToken = useRef<string | null>(null)
  // Local "expanded card" state mirrors the global current-workspace
  // selection on mount so the page opens to whatever the rest of the app
  // is acting on — but lets the user expand a different card without
  // switching their global context. Switching IS done by clicking
  // "Switch to this workspace" inside the detail panel.
  const [selectedUuid, setSelectedUuid] = useState<string | null>(
    () => currentWorkspaceUuid,
  )
  // Use the "store previous render information" pattern so the local
  // selection picks up a later-arriving global selection (workspaces list
  // resolves after mount) without bouncing through an effect — calling
  // setState during render schedules an immediate retry but skips the paint.
  const [observedGlobalUuid, setObservedGlobalUuid] = useState<string | null>(
    currentWorkspaceUuid,
  )
  if (observedGlobalUuid !== currentWorkspaceUuid) {
    setObservedGlobalUuid(currentWorkspaceUuid)
    if (currentWorkspaceUuid && selectedUuid === null) {
      setSelectedUuid(currentWorkspaceUuid)
    }
  }

  const selectedWorkspace =
    workspaces?.find((w) => w.uuid === selectedUuid) ?? null

  const handleCreated = (uuid: string) => {
    setSelectedUuid(uuid)
    setCurrentWorkspace(uuid)
  }

  useEffect(() => {
    if (!inviteToken || attemptedInviteToken.current === inviteToken) return
    attemptedInviteToken.current = inviteToken

    let cancelled = false
    void acceptInvite
      .mutateAsync({ token: inviteToken })
      .then((workspace) => {
        if (cancelled) return
        toast.success(`Joined ${workspace.name}`)
        setSelectedUuid(workspace.uuid)
        setCurrentWorkspace(workspace.uuid)
        clearInviteTokenFromLocation()
      })
      .catch((err: unknown) => {
        if (cancelled) return
        toast.error(formatError(err))
      })

    return () => {
      cancelled = true
    }
  }, [acceptInvite, inviteToken, setCurrentWorkspace])

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 px-4 py-8 md:px-8">
      <PageHeader
        eyebrow="Collaboration"
        title="Workspaces"
        description="Manage your shared workspaces and projects."
        actions={<CreateWorkspaceDialog onCreated={handleCreated} />}
      />

      {isLoading && (
        <SkeletonList
          rows={3}
          rowClassName="h-[76px]"
          aria-label="Loading workspaces"
        />
      )}

      {!isLoading && error && (
        <InlineError
          title="Failed to load workspaces"
          message={formatError(error)}
        />
      )}

      {!isLoading && !error && workspaces && (
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          {/* Left: workspace list */}
          <div className="w-full space-y-2 lg:w-1/3 lg:shrink-0">
            {workspaces.length === 0 ? (
              <EmptyState
                icon={Building2}
                title="No workspaces yet"
                description="Create a workspace to collaborate with your team on shared projects."
                action={<CreateWorkspaceDialog onCreated={handleCreated} />}
                data-testid="workspaces-empty"
              />
            ) : (
              workspaces.map((ws) => (
                <WorkspaceCard
                  key={ws.uuid}
                  workspace={ws}
                  selected={ws.uuid === selectedUuid}
                  onClick={() =>
                    setSelectedUuid(ws.uuid === selectedUuid ? null : ws.uuid)
                  }
                />
              ))
            )}
          </div>

          {/* Right: detail panel */}
          <div className="min-w-0 flex-1">
            {selectedWorkspace ? (
              <WorkspaceDetail workspace={selectedWorkspace} />
            ) : (
              <div className="hidden lg:flex lg:h-48 lg:items-center lg:justify-center lg:rounded-lg lg:border lg:border-dashed lg:border-border lg:text-sm lg:text-muted-foreground">
                Select a workspace to view details
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
